/**
 * P383.2 -- the guided connection setup flow.
 *
 * Setting up one connected system is a sequence: pick it, enter
 * credentials, prove they work, then turn it on. That sequence was
 * previously three independent cells inside an expanded row on a
 * 13-row grid, with nothing stating the order and nothing saying what
 * to enter or where to get it.
 *
 * Test gates enable, deliberately. The instinct is to enable and see
 * what happens -- which is exactly how three connectors ended up
 * pointed at hostnames that did not exist, failing on a cadence
 * forever (P382 F2). Here, turning a connector on is evidence-backed.
 * The grid still allows a direct enable; this is the guided path, not
 * a lock.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { Link } from "react-router-dom";

import { ApiError } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { useConnectionReadiness } from "shared/api/hooks/useConnectionReadiness";
import { useSetupGuides } from "shared/api/hooks/useSetupGuides";
import {
  useConnectorCredentials,
  useSetConnectorCredential,
  useClearConnectorCredential,
  useTestConnector,
} from "shared/api/hooks/useConnectorCredentials";
import { useUpdateConnector } from "shared/api/hooks/useToggleConnectorEnabled";
import {
  useConnectorRuns,
  useConnectors,
  useTriggerConnectorRun,
} from "shared/api/hooks/useConnectors";
import { LiveRunsPanel } from "shared/admin/LiveRunsPanel";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { EngineBusy } from "shared/components/EngineBusy";
import { useToast } from "shared/toast/ToastProvider";
import type {
  ConnectorConfigField,
  ConnectorReadiness,
  ConnectorTestResult,
  SetupGuide,
} from "shared/api/types";

const READINESS_TONE: Record<string, string> = {
  verified: "bg-emerald-100 text-emerald-800",
  configured: "bg-slate-100 text-slate-700",
  not_configured: "bg-amber-100 text-amber-800",
  failing: "bg-rose-100 text-rose-800",
};

function StepHeading({
  n,
  title,
  active,
}: {
  n: number;
  title: string;
  active: boolean;
}): JSX.Element {
  return (
    <h2
      className={`mb-2 flex items-center gap-2 font-display text-lg ${
        active ? "text-slate-900" : "text-slate-400"
      }`}
    >
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
          active ? "bg-brand-brass text-brand-navy" : "bg-slate-200 text-slate-500"
        }`}
      >
        {n}
      </span>
      {title}
    </h2>
  );
}

function GuidePanel({ guide }: { guide: SetupGuide }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid="setup-guide"
      className="mb-3 rounded border border-paper-2 bg-paper px-3 py-2 text-xs"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold text-slate-800">{guide.system}</span>
        {guide.confidence === "unverified" ? (
          <span
            data-testid="setup-guide-unverified"
            className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800"
            title={t("screens.connectionSetupView.theFieldListIsExact")}
          >
            {t("screens.connectionSetupView.stepsNotYetVerifiedAgainstALive")}
          </span>
        ) : guide.confidence === "contract" ? (
          // A third state, not a variant of "verified": there is no
          // vendor to authenticate against. Falling through to the
          // emerald badge would claim interop we have not shown.
          <span
            data-testid="setup-guide-contract"
            className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800"
            title={t("screens.connectionSetupView.noThirdPartyVendorTo")}
          >
            {t("screens.connectionSetupView.contractWePublishExercisedEndToEnd")}
          </span>
        ) : (
          <span
            data-testid="setup-guide-verified"
            className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800"
          >
            {t("screens.connectionSetupView.verifiedAgainstALiveSystem")}
          </span>
        )}
      </div>
      {guide.protocol_defect && (
        // P438.0 -- stated before any credential is entered: no
        // configuration makes this adapter reach a real system.
        <div
          role="alert"
          data-testid="setup-guide-protocol-defect"
          className="mb-2 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-rose-800"
        >
          <p className="font-semibold">
            {t("screens.connectionSetupView.cannotReachARealSystemYet")}
          </p>
          <p>{guide.protocol_defect}</p>
        </div>
      )}
      <p className="mb-2 text-slate-600">{guide.summary}</p>
      {guide.steps.length > 0 && (
        <ol className="mb-2 list-decimal space-y-0.5 ps-4 text-slate-600">
          {guide.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      )}
      {guide.gotchas.length > 0 && (
        <div data-testid="setup-guide-gotchas">
          <p className="font-semibold text-slate-700">
            {t("screens.connectionSetupView.knownTraps")}
          </p>
          <ul className="list-disc space-y-0.5 ps-4 text-slate-600">
            {guide.gotchas.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}
      {guide.doc_url && (
        <a
          href={guide.doc_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block text-brand-brassDark hover:underline"
        >
          {t("screens.connectionSetupView.vendorDocumentation")}
        </a>
      )}
    </div>
  );
}

/**
 * P384.2 -- collect the config a connector cannot start without.
 *
 * Config lives on `Connector.config`, not in the credential spec, so
 * the P383 flow could not ask for it. Connecting Confluence proved the
 * cost: the operator entered the API token, pressed Test, and got
 * "connector.config missing required keys ['atlassian_site_url',
 * 'email']" -- a runtime error standing in for a form field.
 *
 * PATCH replaces the whole config object (P60 semantics), so this
 * MERGES over what is already there rather than overwriting a value
 * the operator set on the grid.
 */
type Drafts = Record<string, string>;

/** Typed values that have not been saved yet. */
function pending(drafts: Drafts): [string, string][] {
  return Object.entries(drafts).filter(([, v]) => v !== "");
}

/**
 * The connector's config with the typed drafts laid over it, or the
 * label of the field whose value will not parse. PATCH replaces the
 * whole object (P60), so this merges rather than overwriting a value
 * set on the grid.
 */
function mergeConfigDrafts(
  fields: ConnectorConfigField[],
  drafts: Drafts,
  currentConfig: Record<string, unknown>,
): { config: Record<string, unknown> } | { invalid: string } {
  const merged: Record<string, unknown> = { ...currentConfig };
  for (const f of fields) {
    const v = drafts[f.name];
    if (v === undefined || v === "") continue;
    // A JSON placeholder means the vendor wants a list or an object.
    const shape = f.placeholder.trim();
    if (shape.startsWith("[") || shape.startsWith("{")) {
      try {
        merged[f.name] = JSON.parse(v);
      } catch {
        return { invalid: f.label };
      }
    } else {
      merged[f.name] = v;
    }
  }
  return { config: merged };
}

function useCurrentConfig(connectorId: string): Record<string, unknown> {
  const connectors = useConnectors();
  const rows = connectors.data?.connectors ?? [];
  const current = rows.find((r) => r.id === connectorId);
  return (current?.config ?? {}) as Record<string, unknown>;
}

function ConfigStep({
  connector,
  fields,
  drafts,
  setDrafts,
}: {
  connector: ConnectorReadiness;
  fields: ConnectorConfigField[];
  drafts: Drafts;
  setDrafts: (update: (d: Drafts) => Drafts) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const update = useUpdateConnector();
  const toast = useToast();
  const currentConfig = useCurrentConfig(connector.connector_id);

  /* An all-optional config (ServiceNow CMDB's api_mode) still has to be
     reachable: hiding the step when nothing is REQUIRED left the one
     setting a real instance needs with no field to type it into. */
  if (fields.length === 0) return null;

  /* PATCH replaces the whole object (P60), so clearing a value is
     saving the current config without that key. An empty input means
     "no change" -- the draft rules -- so a clear needs its own act. */
  const onClear = (name: string, label: string) => {
    const next = Object.fromEntries(
      Object.entries(currentConfig).filter(([k]) => k !== name),
    );
    update.mutate(
      { connectorId: connector.connector_id, config: next },
      {
        onSuccess: () => {
          setDrafts((d) => ({ ...d, [name]: "" }));
          toast.push({
            tone: "success",
            message: t("screens.connectionSetupView.cleared", { label }),
          });
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  };

  const onSave = () => {
    const merged = mergeConfigDrafts(fields, drafts, currentConfig);
    if ("invalid" in merged) {
      toast.push({
        tone: "error",
        message: t("screens.connectionSetupView.mustBeValidJson", {
          label: merged.invalid,
        }),
      });
      return;
    }
    update.mutate(
      { connectorId: connector.connector_id, config: merged.config },
      {
        onSuccess: () => {
          setDrafts(() => ({}));
          toast.push({ tone: "success", message: "Configuration saved" });
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  };

  return (
    <div data-testid="setup-step-config" className="mb-4">
      <ul className="mb-2 space-y-1">
        {fields.map((f) => {
          const current = currentConfig[f.name];
          const set = current !== undefined && current !== null && current !== "";
          /* Config is not secret (the secret lives in the credential
             store), so say what it holds: an operator who sees only
             "set" cannot tell a wrong value from a right one. */
          const shown = set
            ? typeof current === "string"
              ? current
              : JSON.stringify(current)
            : "";
          return (
            <li
              key={f.name}
              data-testid={`setup-config-${f.name}`}
              className="flex items-start gap-2 text-xs"
            >
              <span className="w-56 shrink-0 pt-0.5 text-slate-700">
                {f.label}
                {!f.required && (
                  <span className="text-slate-400">
                    {" "}
                    {t("screens.connectionSetupView.optional")}
                  </span>
                )}
              </span>
              <span className="flex-1">
                <input
                  dir="auto"
                  value={drafts[f.name] ?? ""}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [f.name]: e.target.value }))
                  }
                  placeholder={f.placeholder || (set ? "set" : "")}
                  aria-label={f.label}
                  className="w-full rounded border border-slate-300 px-1.5 py-0.5"
                />
                {set && (
                  <span
                    data-testid={`setup-config-current-${f.name}`}
                    className="block break-all text-[10px] text-slate-600"
                  >
                    {t("screens.connectionSetupView.currently", { value: shown })}
                  </span>
                )}
                {f.help && <span className="text-[10px] text-slate-500">{f.help}</span>}
              </span>
              <span className="w-12 shrink-0 pt-0.5 text-[10px] text-slate-500">
                {set
                  ? t("screens.connectionSetupView.set")
                  : t("screens.connectionSetupView.notSet")}
              </span>
              {set && (
                <button
                  type="button"
                  onClick={() => onClear(f.name, f.label)}
                  data-testid={`setup-config-clear-${f.name}`}
                  className="shrink-0 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-700"
                >
                  {t("screens.connectionSetupView.clear")}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={onSave}
        data-testid="setup-config-save"
        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-[11px] font-semibold text-brand-brassDark"
      >
        {t("screens.connectionSetupView.saveConfiguration")}
      </button>
    </div>
  );
}

function CredentialsStep({
  connector,
  guide,
  onTested,
}: {
  connector: ConnectorReadiness;
  guide: SetupGuide | undefined;
  onTested: (r: ConnectorTestResult) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const creds = useConnectorCredentials(connector.connector_id);
  const setField = useSetConnectorCredential();
  const clearField = useClearConnectorCredential();
  const update = useUpdateConnector();
  const test = useTestConnector();
  const toast = useToast();
  const currentConfig = useCurrentConfig(connector.connector_id);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [configDrafts, setConfigDrafts] = useState<Drafts>({});
  const [saving, setSaving] = useState(false);
  // Kept on the page, not only in a toast: a toast fades and takes the
  // reason with it (the E1 lesson about a failed test).
  const [saveError, setSaveError] = useState<string | null>(null);
  const configFields = guide?.config_fields ?? [];
  const unsaved = pending(drafts).length + pending(configDrafts).length;

  const onSave = (fieldName: string) => {
    const value = drafts[fieldName] ?? "";
    if (!value) return;
    setField.mutate(
      { connectorId: connector.connector_id, fieldName, value },
      {
        onSuccess: () => {
          setDrafts((d) => ({ ...d, [fieldName]: "" }));
          toast.push({ tone: "success", message: `${fieldName} saved` });
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  };

  /* Only a STORED value can be cleared here: one read from the
     deployment's environment is not ours to remove, and clearing the
     stored value is what lets the environment's show through again. */
  const onClear = (fieldName: string, label: string) => {
    clearField.mutate(
      { connectorId: connector.connector_id, fieldName },
      {
        onSuccess: () => {
          setDrafts((d) => ({ ...d, [fieldName]: "" }));
          toast.push({
            tone: "success",
            message: t("screens.connectionSetupView.cleared", { label }),
          });
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  };

  /**
   * ROUND 15 (P427 live run) -- the operator typed every Archer
   * credential, pressed Test, and got "missing credentials": each field
   * saved only through its own button and Test read none of what was
   * typed. Test now saves what is pending -- config, then each
   * credential -- and tests only once all of it has saved, so it never
   * tests values other than the ones on screen. A failed save stops
   * the test and says which value.
   */
  const saveTypedValues = async (): Promise<boolean> => {
    if (pending(configDrafts).length > 0) {
      const merged = mergeConfigDrafts(configFields, configDrafts, currentConfig);
      if ("invalid" in merged) {
        setSaveError(
          t("screens.connectionSetupView.mustBeValidJson", { label: merged.invalid }),
        );
        return false;
      }
      await update.mutateAsync({
        connectorId: connector.connector_id,
        config: merged.config,
      });
      setConfigDrafts(() => ({}));
    }
    for (const [fieldName, value] of pending(drafts)) {
      await setField.mutateAsync({
        connectorId: connector.connector_id,
        fieldName,
        value,
      });
      setDrafts((d) => ({ ...d, [fieldName]: "" }));
    }
    return true;
  };

  const onTest = async () => {
    setSaveError(null);
    if (unsaved > 0) {
      setSaving(true);
      try {
        if (!(await saveTypedValues())) return;
      } catch (e) {
        setSaveError(
          t("screens.connectionSetupView.notTestedSaveFailed", {
            // The server's own words, not the client's "API 422:" prefix.
            reason:
              e instanceof ApiError
                ? e.detail
                : e instanceof Error
                  ? e.message
                  : String(e),
          }),
        );
        return;
      } finally {
        setSaving(false);
      }
    }
    test.mutate(connector.connector_id, {
      onSuccess: (r) => {
        onTested(r);
        toast.push({
          tone: r.ok ? "success" : "warning",
          message: r.ok ? "Connection succeeded" : `Connection failed: ${r.error}`,
        });
      },
      onError: (e) => toast.push({ tone: "error", message: e.message }),
    });
  };

  return (
    <div data-testid="setup-step-credentials">
      {guide && <GuidePanel guide={guide} />}
      {guide && (
        <ConfigStep
          connector={connector}
          fields={configFields}
          drafts={configDrafts}
          setDrafts={setConfigDrafts}
        />
      )}

      {creds.data && !creds.data.encryption_configured && (
        <p
          data-testid="setup-no-encryption-key"
          className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          {t("screens.connectionSetupView.marsadCredentialEncryptionKeyIsNotSet")}
        </p>
      )}

      <ul className="mb-3 space-y-1">
        {(creds.data?.fields ?? []).map((f) => (
          <li
            key={f.name}
            data-testid={`setup-field-${f.name}`}
            className="flex items-center gap-2 text-xs"
          >
            <span className="w-56 shrink-0 text-slate-700">
              {f.label}
              {!f.required && (
                <span className="text-slate-400">
                  {" "}
                  {t("screens.connectionSetupView.optional")}
                </span>
              )}
            </span>
            <input
              dir="auto"
              type={f.secret ? "password" : "text"}
              value={drafts[f.name] ?? ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [f.name]: e.target.value }))}
              placeholder={f.status === "not_set" ? "not set" : f.status}
              aria-label={f.label}
              className="w-64 rounded border border-slate-300 px-1.5 py-0.5"
            />
            <button
              type="button"
              onClick={() => onSave(f.name)}
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 font-semibold text-brand-brassDark"
            >
              {t("screens.connectionSetupView.save")}
            </button>
            <span className="text-[10px] text-slate-500">{f.status}</span>
            {f.status === "stored" && (
              <button
                type="button"
                onClick={() => onClear(f.name, f.label)}
                data-testid={`setup-field-clear-${f.name}`}
                className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-700"
              >
                {t("screens.connectionSetupView.clear")}
              </button>
            )}
          </li>
        ))}
      </ul>

      {saving || test.isPending ? (
        <EngineBusy
          label={
            saving
              ? t("screens.connectionSetupView.savingBeforeTest")
              : t("screens.connectionSetupView.testingConnection")
          }
          testid="setup-testing"
        />
      ) : (
        <button
          type="button"
          onClick={() => void onTest()}
          data-testid="setup-test"
          data-unsaved={unsaved}
          className="rounded bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy"
        >
          {unsaved > 0
            ? t("screens.connectionSetupView.saveAndTestConnection")
            : t("screens.connectionSetupView.testConnection")}
        </button>
      )}
      {saveError && (
        <p
          role="alert"
          data-testid="setup-save-failed"
          className="mt-2 rounded border border-rag-red/40 bg-rag-red/5 px-2 py-1 text-[11px] text-slate-800"
        >
          {saveError}
        </p>
      )}
    </div>
  );
}

/**
 * D9 -- is the system that refused us OUR OWN?
 *
 * Compared by hostname rather than by connector kind: what matters is
 * where the request went, and `architecture_edges` is configurable to
 * either a bank's EA tool or this deployment's own exporter. A false
 * negative just leaves the old wording, which is the safe direction.
 */
function failingHostIsThisDeployment(url: unknown): boolean {
  if (typeof url !== "string") return false;
  try {
    return new URL(url, window.location.origin).hostname === window.location.hostname;
  } catch {
    return false;
  }
}

function ActivateStep({
  connector,
  result,
}: {
  connector: ConnectorReadiness;
  result: ConnectorTestResult;
}): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateConnector();
  const run = useTriggerConnectorRun();
  const toast = useToast();
  const [cadence, setCadence] = useState("300");
  // D9 -- the URL the failing request went to, for the sentence below.
  const connectors = useConnectors();
  const configuredUrl = (
    (connectors.data?.connectors ?? []).find((r) => r.id === connector.connector_id)
      ?.config as Record<string, unknown> | null | undefined
  )?.["url"];

  if (!result.ok) {
    // E1 -- a failed test left only a toast, and once it faded the
    // panel said the step "has not passed yet" -- which reads as
    // "you have not run it" rather than "it ran and here is why it
    // failed". The vendor's own words are the whole diagnosis, and
    // they were being thrown away.
    return (
      <div data-testid="setup-blocked" className="space-y-1 text-xs">
        <p className="text-rose-800">
          {t("screens.connectionSetupView.testFailedHeading")}
        </p>
        {result.error && (
          <p
            data-testid="setup-test-error"
            className="rounded border border-rose-200 bg-rose-50 px-2 py-1 font-mono text-[11px] text-rose-900"
          >
            {result.error}
          </p>
        )}
        {/* D9 (round 8) -- whose permission it is depends on WHOSE
            system refused.
            The sentence "the person who administers that system is who
            unblocks it -- not MARSAD" was printed unconditionally,
            including for a 403 against MARSAD's OWN exporter URL --
            which is the only URL this connector's own guide recommends
            trying. The operator was sent to find an administrator of
            the system he was already administering. Second round. */}
        <p className="text-slate-600">
          {failingHostIsThisDeployment(configuredUrl)
            ? t("screens.connectionSetupView.testFailedOurs")
            : t("screens.connectionSetupView.testFailedEscalate")}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="setup-step-activate" className="space-y-2 text-xs">
      <p className="text-emerald-700">
        {t("screens.connectionSetupView.connectedIn")} {result.latency_ms}
        {t("screens.connectionSetupView.ms")}
        {result.records_in_first_batch !== null &&
          ` — first batch returned ${result.records_in_first_batch} record(s)`}
      </p>
      <label className="flex items-center gap-2">
        <span className="text-slate-700">
          {t("screens.connectionSetupView.pollEvery")}
        </span>
        <input
          dir="auto"
          value={cadence}
          onChange={(e) => setCadence(e.target.value)}
          aria-label="Cadence in seconds"
          className="w-24 rounded border border-slate-300 px-1.5 py-0.5"
        />
        <span className="text-slate-600">
          {t("screens.connectionSetupView.seconds")}
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="setup-enable"
          onClick={() =>
            update.mutate(
              {
                connectorId: connector.connector_id,
                enabled: true,
                cadence_seconds: Number(cadence) || null,
              },
              {
                onSuccess: () =>
                  toast.push({ tone: "success", message: "Connector enabled" }),
                onError: (e) => toast.push({ tone: "error", message: e.message }),
              },
            )
          }
          className="rounded bg-brand-brass px-3 py-1 font-semibold text-brand-navy"
        >
          {t("screens.connectionSetupView.enableOnThisCadence")}
        </button>
        <button
          type="button"
          data-testid="setup-run-now"
          onClick={() =>
            run.mutate(connector.connector_id, {
              onSuccess: () =>
                toast.push({ tone: "info", message: "First run queued" }),
              onError: (e) => toast.push({ tone: "error", message: e.message }),
            })
          }
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 font-semibold text-brand-brassDark"
        >
          {t("screens.connectionSetupView.runItNow")}
        </button>
      </div>
      <LiveRunsPanel connectorId={connector.connector_id} />
      <LatestRunLine connectorId={connector.connector_id} />
    </div>
  );
}

/**
 * How the connector's latest run ended. The live panel goes when the
 * run ends; this is what replaces it, keyed on the connector row's own
 * last-run marker so it refreshes the moment the run is recorded.
 */
function LatestRunLine({ connectorId }: { connectorId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const connectors = useConnectors();
  const row = (connectors.data?.connectors ?? []).find((r) => r.id === connectorId);
  const rowVersion = row
    ? `${row.last_run_at ?? ""}|${row.last_run_status ?? ""}|${row.error_streak}`
    : "";
  const runs = useConnectorRuns(connectorId, { limit: 1, rowVersion });
  const latest = Array.isArray(runs.data) ? runs.data[0] : undefined;
  if (!latest || latest.status === "running") return null;
  const ok = latest.status === "succeeded";
  return (
    <p
      data-testid="setup-latest-run"
      data-status={latest.status}
      className={ok ? "text-slate-700" : "text-rose-800"}
    >
      {ok
        ? t("screens.liveRuns.lastSucceeded", {
            inserted: latest.row_inserted,
            updated: latest.row_updated,
            skipped: latest.row_skipped,
          })
        : t("screens.liveRuns.lastFailed", { reason: latest.error_message ?? "" })}
    </p>
  );
}

export function ConnectionSetupView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const readiness = useConnectionReadiness();
  const guides = useSetupGuides();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<ConnectorTestResult | null>(null);

  if (!(me.data?.is_admin ?? false)) {
    return <AdminOnlyPanel surface="Connection setup" />;
  }

  const all = Array.isArray(readiness.data?.connectors)
    ? readiness.data.connectors
    : [];
  const selected = all.find((c) => c.connector_id === selectedId) ?? null;
  const guide = (guides.data ?? []).find((g) => g.kind === selected?.kind);

  return (
    <article data-testid="connection-setup" className="max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-2xl">
          <MarsadIcon
            name="telemetry"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.connectionSetupView.connectASystem")}
        </h1>
        <p className="text-slate-600">
          {t("screens.connectionSetupView.credentialsThenProofItWorksThenTurn")}{" "}
          <Link to="/admin/connectors" className="text-brand-brassDark hover:underline">
            {t("screens.connectionSetupView.connectors")}
          </Link>
          .
        </p>
      </header>

      <section>
        <StepHeading n={1} title="Choose a system" active={!selected} />
        <ul className="space-y-1">
          {all.map((c) => (
            <li key={c.connector_id}>
              <button
                type="button"
                data-testid={`setup-pick-${c.kind}`}
                onClick={() => {
                  setSelectedId(c.connector_id);
                  setResult(null);
                }}
                className={`flex w-full items-center justify-between rounded border px-2 py-1 text-start text-xs ${
                  c.connector_id === selectedId
                    ? "border-brand-brass bg-brand-brass/5"
                    : "border-paper-2 bg-white"
                }`}
              >
                <span className="font-medium text-slate-700">{c.label}</span>
                {/* C5 -- fifth round. A disabled connector that has
                    never run read "configured" against its own table
                    row saying otherwise. "Configured" is true --
                    credentials resolve -- and it oversells, because a
                    switched-off connector is not going to run and the
                    word invites the reader to think it is working.
                    Readiness already carries `enabled`; the pill just
                    never said it. */}
                <span
                  data-testid={`setup-readiness-${c.kind}`}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                    c.enabled
                      ? (READINESS_TONE[c.readiness] ?? "bg-slate-100 text-slate-700")
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {/* ROUND 13 H -- and the word itself. `readiness`
                      is an enum token, and `.replace("_", " ")` put it
                      on screen as prose it never was: untranslatable,
                      and "configured" claims an act by the BANK where
                      the state means only that credentials resolve --
                      which on a fresh morning they do, from the
                      deployment's own environment, for eleven of
                      thirteen systems nobody has touched. Say what the
                      state means instead of what it is called. */}
                  {c.enabled
                    ? t(`screens.connectionSetupView.readiness.${c.readiness}`, {
                        defaultValue: c.readiness.replace("_", " "),
                      })
                    : t("screens.connectionSetupView.readinessOff", {
                        state: t(
                          `screens.connectionSetupView.readiness.${c.readiness}`,
                          { defaultValue: c.readiness.replace("_", " ") },
                        ),
                      })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {selected && (
        <>
          <section>
            <StepHeading
              n={2}
              title={t("screens.connectionSetupView.credentialsAndProveTheyWork")}
              active
            />
            <CredentialsStep connector={selected} guide={guide} onTested={setResult} />
          </section>
          <section>
            <StepHeading
              n={3}
              title={t("screens.connectionSetupView.turnItOn")}
              active={Boolean(result?.ok)}
            />
            {result ? (
              <ActivateStep connector={selected} result={result} />
            ) : (
              <p data-testid="setup-untested" className="text-xs text-slate-600">
                {t("screens.connectionSetupView.runAConnectionTestFirstEnablingA")}
              </p>
            )}
          </section>
        </>
      )}
    </article>
  );
}
